/* eslint-disable max-lines */
/* eslint-disable react/prop-types */
/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import FormDialog from '../../Components/Modal/modal';
import './PODetailsModal.scss';

const PO_STATUS_OPTIONS = ['In Progress', 'On Hold', 'Closed', 'Dropped'];

const normalizePoStatus = (status) => {
  const normalized = String(status || '').trim();
  return PO_STATUS_OPTIONS.includes(normalized) ? normalized : '';
};

const PODetailsModal = ({
  open,
  isEdit,
  initialData,
  clients,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    po_number: '',
    client_id: '',
    client_name: '',
    amount: '',
    start_date: '',
    received_date: '',
    expire_date: '',
    po_status: '',
    po_links: [],
    credit_period: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && initialData) {
      setFormData({
        po_number: initialData.po_number || '',
        client_id: initialData.client_id || '',
        client_name: initialData.client_name || '',
        amount: initialData.amount || '',
        start_date: initialData.start_date || '',
        received_date: initialData.received_date || '',
        expire_date: initialData.expire_date || '',
        po_status: normalizePoStatus(initialData.po_status),
        po_links: Array.isArray(initialData.po_links) ? initialData.po_links : [],
        credit_period: initialData.credit_period || '',
      });
    } else {
      setFormData({
        po_number: '',
        client_id: '',
        client_name: '',
        amount: '',
        start_date: '',
        received_date: '',
        expire_date: '',
        po_status: '',
        po_links: [],
        credit_period: '',
      });
    }
    setErrors({});
  }, [open, isEdit, initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.po_number.trim()) {
      newErrors.po_number = 'PO Number is required';
    }

    if (!formData.client_id) {
      newErrors.client_id = 'Zoho Client Name is required';
    }

    if (!formData.received_date) {
      newErrors.received_date = 'Received Date is required';
    }

    if (!formData.po_status) {
      newErrors.po_status = 'Status is required';
    }

    if (!String(formData.credit_period).trim()) {
      newErrors.credit_period = 'Credit Period is required';
    }

    // Optional validations (when field is provided)
    if (formData.amount && (isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0)) {
      newErrors.amount = 'Amount must be a valid positive number';
    }

    if (formData.expire_date && formData.start_date) {
      if (new Date(formData.expire_date) <= new Date(formData.start_date)) {
        newErrors.expire_date = 'Expire Date must be after Start Date';
      }
    }

    if (String(formData.credit_period).trim()) {
      if (!Number.isInteger(parseFloat(formData.credit_period)) || parseFloat(formData.credit_period) < 0) {
        newErrors.credit_period = 'Credit Period must be a non-negative whole number';
      }
    }

    if (!formData.po_links[0]?.trim()) {
      newErrors.po_links = 'PO Link is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (evt) => {
    const { name, value } = evt.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleClientChange = (evt) => {
    const selectedClientId = evt.target.value;
    const clientIdNum = selectedClientId ? parseInt(selectedClientId, 10) : '';
    const selectedClient = clients.find((client) => String(client.id) === String(clientIdNum));
    setFormData((prev) => ({
      ...prev,
      client_id: clientIdNum,
      client_name: selectedClient ? selectedClient.clientName : '',
    }));
    if (errors.client_id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.client_id;
        return newErrors;
      });
    }
  };

  const handlePoLinkChange = (evt) => {
    const { value } = evt.target;
    setFormData((prev) => ({
      ...prev,
      po_links: value ? [value] : [],
    }));
    if (errors.po_links) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.po_links;
        return newErrors;
      });
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Ensure client_id is an integer and po_number is a string
      const clientIdNum = parseInt(formData.client_id, 10);

      if (isNaN(clientIdNum) || !formData.po_number?.trim()) {
        const missingFields = [];
        if (!formData.po_number?.trim()) missingFields.push('PO Number');
        if (isNaN(clientIdNum)) missingFields.push('valid Zoho Client Name');
        const errorMsg = `Missing required fields: ${missingFields.join(', ')}`;
        toast.error(errorMsg);
        return;
      }

      const normalizedPoStatus = normalizePoStatus(formData.po_status);

      const submitData = {
        po_number: formData.po_number.trim(),
        client_id: clientIdNum,
        amount: formData.amount ? parseFloat(formData.amount) : undefined,
        start_date: formData.start_date || undefined,
        received_date: formData.received_date || undefined,
        expire_date: formData.expire_date || undefined,
        po_status: normalizedPoStatus,
        po_link: formData.po_links[0].trim(),
        credit_period: parseInt(formData.credit_period, 10),
      };

      onSubmit(submitData);
    }
  };

  const dialogContent = (
    <div className="po-details-modal-content">
      {/* PO Number & Client Row */}
      <div className="form-row">
        <div className="form-group col-md-6">
          <label htmlFor="po_number">PO Number <span className="required">*</span></label>
          <input
            type="text"
            className={`form-control ${errors.po_number ? 'is-invalid' : ''}`}
            id="po_number"
            name="po_number"
            value={formData.po_number}
            onChange={handleInputChange}
            disabled={isLoading}
            placeholder="Enter PO Number"
          />
          {errors.po_number && <span className="invalid-feedback">{errors.po_number}</span>}
        </div>
        <div className="form-group col-md-6">
          <label htmlFor="client_id">Zoho Client Name <span className="required">*</span></label>
          <select
            className={`form-control ${errors.client_id ? 'is-invalid' : ''}`}
            id="client_id"
            value={formData.client_id}
            onChange={handleClientChange}
            disabled={isLoading || clients.length === 0}
          >
            <option value="">Select</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.clientName}
              </option>
            ))}
          </select>
          {errors.client_id && <span className="invalid-feedback">{errors.client_id}</span>}
        </div>
      </div>

      {/* Amount & Start Date Row */}
      <div className="form-row">
        <div className="form-group col-md-6">
          <label htmlFor="amount">Amount (INR) <span className="required">*</span></label>
          <input
            type="number"
            className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={handleInputChange}
            disabled={isLoading}
            placeholder="Enter Amount"
            step="0.01"
          />
          {errors.amount && <span className="invalid-feedback">{errors.amount}</span>}
        </div>
        <div className="form-group col-md-6">
          <label htmlFor="start_date">Start Date</label>
          <input
            type="date"
            className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
            id="start_date"
            name="start_date"
            value={formData.start_date}
            onChange={handleInputChange}
            disabled={isLoading}
            placeholder="Choose date"
          />
          {errors.start_date && <span className="invalid-feedback">{errors.start_date}</span>}
        </div>
      </div>

      {/* Received Date & Expire Date Row */}
      <div className="form-row">
        <div className="form-group col-md-6">
          <label htmlFor="received_date">Received Date <span className="required">*</span></label>
          <input
            type="date"
            className={`form-control ${errors.received_date ? 'is-invalid' : ''}`}
            id="received_date"
            name="received_date"
            value={formData.received_date}
            onChange={handleInputChange}
            disabled={isLoading}
            placeholder="Choose date"
          />
          {errors.received_date && <span className="invalid-feedback">{errors.received_date}</span>}
        </div>
        <div className="form-group col-md-6">
          <label htmlFor="expire_date">Expire Date</label>
          <input
            type="date"
            className={`form-control ${errors.expire_date ? 'is-invalid' : ''}`}
            id="expire_date"
            name="expire_date"
            value={formData.expire_date}
            onChange={handleInputChange}
            disabled={isLoading}
            placeholder="Choose date"
          />
          {errors.expire_date && <span className="invalid-feedback">{errors.expire_date}</span>}
        </div>
      </div>

      {/* Status & Credit Period Row */}
      <div className="form-row">
        <div className="form-group col-md-6">
          <label htmlFor="po_status">Status <span className="required">*</span></label>
          <select
            className={`form-control ${errors.po_status ? 'is-invalid' : ''}`}
            id="po_status"
            name="po_status"
            value={formData.po_status}
            onChange={handleInputChange}
            disabled={isLoading}
          >
            <option value="">Select</option>
            {PO_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          {errors.po_status && <span className="invalid-feedback">{errors.po_status}</span>}
        </div>
        <div className="form-group col-md-6">
          <label htmlFor="credit_period">Credit Period (Days) <span className="required">*</span></label>
          <input
            type="number"
            className={`form-control ${errors.credit_period ? 'is-invalid' : ''}`}
            id="credit_period"
            name="credit_period"
            value={formData.credit_period}
            onChange={handleInputChange}
            disabled={isLoading}
            placeholder="Enter Credit Period (Days)"
            min="0"
          />
          {errors.credit_period && <span className="invalid-feedback">{errors.credit_period}</span>}
        </div>
      </div>

      {/* PO Links */}
      <div className="form-group">
        <label>PO Link <span className="required">*</span></label>
        <input
          type="url"
          className={`form-control ${errors.po_links ? 'is-invalid' : ''}`}
          placeholder="Enter PO Link"
          value={formData.po_links[0] || ''}
          onChange={handlePoLinkChange}
          disabled={isLoading}
        />
        {errors.po_links && <span className="invalid-feedback">{errors.po_links}</span>}
      </div>
    </div>
  );

  return (
    <FormDialog
      title={isEdit ? 'Edit PO Details' : 'Add PO Details'}
      open={open}
      handleClose={onCancel}
      dialogBodycontent={dialogContent}
      button1="Cancel"
      button2="Save"
      button1Action={onCancel}
      button2Action={handleSubmit}
      button2Disabled={isLoading}
      dialogCss="po-details-dialog"
    />
  );
};

PODetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired,
  initialData: PropTypes.object,
  clients: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

PODetailsModal.defaultProps = {
  initialData: null,
};

export default PODetailsModal;
